/**
 * OAuth 2.0 PKCE Authentication Service
 *
 * Handles the full OAuth 2.0 Authorization Code flow with PKCE for SPAs.
 * Supports multiple auth providers (Cognito, IdPrism, Federate).
 *
 * Flow:
 * 1. User clicks a provider button on the login page
 * 2. login(provider) generates PKCE codes, stores state, redirects to Hosted UI
 * 3. Provider redirects back to /callback?code=xxx&state=xxx
 * 4. handleCallback() exchanges the code for tokens
 * 5. Tokens are stored in localStorage (persists across browser restarts)
 * 6. getAccessToken() returns the current token (auto-refreshes if expired)
 */

import { AuthProvider, backendOrigin, getProviderConfig, ProviderConfig } from "@services/env";
import { IUser } from "@type/user";

// ============================================================================
// Types
// ============================================================================

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number; // Unix timestamp in ms
  provider: AuthProvider;
}

interface IdTokenPayload {
  sub: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  "cognito:username"?: string;
  "cognito:groups"?: string[];
  iss: string;
  aud: string;
  exp: number;
  iat: number;
  token_use: string;
  [key: string]: unknown;
}

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEYS = {
  TOKENS: "radarplus_auth_tokens",
  CODE_VERIFIER: "radarplus_pkce_code_verifier",
  AUTH_STATE: "radarplus_auth_state",
  AUTH_PROVIDER: "radarplus_auth_provider",
  RETURN_URL: "radarplus_auth_return_url",
  // Admin impersonation override (mirrors IMPERSONATE_STORAGE_KEY in authInterceptor.ts).
  // Must be wiped on logout so it can't leak onto the next user on a shared machine.
  IMPERSONATE: "radarplus_on_behalf_of",
} as const;

// Prefixes for per-user client-side data namespaced by the ID token `sub`
// (see getHistoryUserKey). Cleared on logout so one user's recent-visit history
// never surfaces under the next user's session on a shared device.
const PER_USER_STORAGE_PREFIXES = ["identifier::", "identifierName::"] as const;

// ============================================================================
// PKCE Helpers (Web Crypto API)
// ============================================================================

/**
 * Generate a cryptographically random string for PKCE code verifier.
 * Must be between 43-128 characters per RFC 7636.
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate the PKCE code challenge from the code verifier.
 * Uses SHA-256 as required by Cognito.
 */
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Generate a random state parameter to prevent CSRF.
 */
function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Base64url encode a Uint8Array (no padding, URL-safe).
 */
function base64UrlEncode(buffer: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ============================================================================
// Token Helpers
// ============================================================================

/**
 * Resolve the token expiration timestamp (in ms) from the token response.
 *
 * Priority:
 * 1. Use `expires_in` from the response if present (e.g., Cognito returns this)
 * 2. Read the `exp` claim from the **access_token** — the credential actually sent to the backend
 * 3. Fall back to the id_token's `exp`
 * 4. Fallback: assume 1 hour from now
 *
 * `expiresAt` is the single clock that {@link getAccessToken}, {@link ensureAuthenticated} and the
 * refresh scheduling all consult, so it has to describe the token being presented. The access
 * token lives 15 minutes (`accessTokenAge`) and the login id_token lives 60 (`idTokenAge`), so
 * reading the id_token's `exp` first made the app believe it had an hour of validity while the
 * credential it was sending died at fifteen minutes — one needless 401 and retry per login.
 */
function resolveExpiresAt(tokenResponse: TokenResponse): number {
  if (tokenResponse.expires_in) {
    return Date.now() + tokenResponse.expires_in * 1000;
  }
  // access_token first: it is the credential. The id_token is the fallback for Cognito, whose
  // operators still authenticate with theirs.
  const jwtCandidates = [tokenResponse.access_token, tokenResponse.id_token].filter(Boolean);
  for (const token of jwtCandidates) {
    try {
      const payload = decodeJwtPayload(token);
      if (payload.exp) {
        return payload.exp * 1000; // exp is Unix seconds → convert to ms
      }
    } catch {
      // Try next candidate
    }
  }
  // Fallback: 1 hour from now
  return Date.now() + 3600 * 1000;
}

/**
 * Decode a JWT token payload without verification.
 * Note: Signature verification should be done server-side.
 */
function decodeJwtPayload(token: string): IdTokenPayload {
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid JWT token format");
  }
  const payload = parts[1];
  // Pad the base64url string to valid base64
  const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
  const decoded = atob(padded);
  return JSON.parse(decoded);
}

/**
 * Store tokens in localStorage (persists across browser restarts).
 */
function storeTokens(tokens: StoredTokens): void {
  localStorage.setItem(STORAGE_KEYS.TOKENS, JSON.stringify(tokens));
}

/**
 * Retrieve stored tokens from localStorage.
 * Using localStorage instead of sessionStorage so sessions persist across browser restarts.
 */
function getStoredTokens(): StoredTokens | null {
  const raw = localStorage.getItem(STORAGE_KEYS.TOKENS);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

/**
 * Clear all auth-related data from storage.
 * Tokens are in localStorage (persistent), PKCE flow data is in sessionStorage (transient).
 */
function clearAuthStorage(): void {
  localStorage.removeItem(STORAGE_KEYS.TOKENS);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_PROVIDER);
}

/**
 * Fully erase everything tied to the current user, for logout.
 *
 * `clearAuthStorage()` only removes the OAuth tokens + transient PKCE flow data,
 * which is right for interrupted-login error paths but leaves user identity behind
 * on logout. This additionally clears:
 *   - the saved return URL,
 *   - the admin impersonation override (must not leak onto the next user), and
 *   - every per-user, `sub`-namespaced history bucket (all identities, so a shared
 *     machine doesn't surface a previous operator's recent-visit history).
 *
 * Non-identity preferences (device id, language, UI toggles) are intentionally kept.
 */
export function clearAllUserData(): void {
  clearAuthStorage();
  localStorage.removeItem(STORAGE_KEYS.RETURN_URL);
  sessionStorage.removeItem(STORAGE_KEYS.RETURN_URL);
  localStorage.removeItem(STORAGE_KEYS.IMPERSONATE);

  // Remove every per-user history bucket regardless of which `sub` it belongs to.
  // Snapshot keys first, since removeItem mutates the store as we iterate.
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && PER_USER_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

// ============================================================================
// Auth Service
// ============================================================================

/**
 * Redirect the user to the provider's Hosted UI for login.
 * Generates PKCE codes and state, stores them, then redirects.
 */
export async function login(provider: AuthProvider): Promise<void> {
  const config = getProviderConfig(provider);
  if (!config) {
    throw new Error(`Unknown auth provider: ${provider}`);
  }
  if (!config.enabled) {
    throw new Error(`Auth provider "${provider}" is not yet available`);
  }

  // Generate PKCE code verifier and challenge
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  // Save the current URL so we can redirect back after login
  const currentUrl = window.location.pathname + window.location.search + window.location.hash;
  if (currentUrl && currentUrl !== "/" && currentUrl !== "/callback") {
    sessionStorage.setItem(STORAGE_KEYS.RETURN_URL, currentUrl);
  }

  // Store PKCE verifier, state, and provider for callback validation
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.AUTH_STATE, state);
  sessionStorage.setItem(STORAGE_KEYS.AUTH_PROVIDER, provider);

  // Build the authorization URL
  const authUrl = buildAuthorizationUrl(config, codeChallenge, state);

  // Redirect to the Hosted UI
  window.location.href = authUrl;
}

/**
 * Build the OAuth 2.0 authorization URL with PKCE parameters.
 */
function buildAuthorizationUrl(config: ProviderConfig, codeChallenge: string, state: string): string {
  const authorizePath = config.authorizePath ?? "/oauth2/authorize";
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  // Use Federate's REQUEST_PARAMETER discovery strategy to route directly to
  // a specific auth provider, bypassing the default discovery logic.
  // See: Federate docs → "Supported Discovery Strategies" → REQUEST_PARAMETER
  // (e.g., MIDWAY for internal users, IDPRISM_RADARPLUSSSO_BETA_NA for CM engineers)
  if (config.idpHint) {
    params.set("auth_provider", config.idpHint);
  }

  return `${config.domain}${authorizePath}?${params.toString()}`;
}

/**
 * Handle the OAuth callback.
 * Extracts the authorization code from the URL, validates state,
 * exchanges the code for tokens, and stores them.
 *
 * @returns The decoded ID token payload with user info.
 * @throws Error if the callback is invalid or token exchange fails.
 */
export async function handleCallback(): Promise<IdTokenPayload> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const returnedState = url.searchParams.get("state");
  const error = url.searchParams.get("error");
  const errorDescription = url.searchParams.get("error_description");

  // Check for errors from the provider
  if (error) {
    clearAuthStorage();
    throw new Error(`Auth error: ${error} - ${errorDescription ?? "Unknown error"}`);
  }

  if (!code) {
    throw new Error("No authorization code found in callback URL");
  }

  // Validate state to prevent CSRF
  const storedState = sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE);
  if (!storedState || storedState !== returnedState) {
    clearAuthStorage();
    throw new Error("Invalid state parameter — possible CSRF attack");
  }

  // Get the stored PKCE code verifier and provider
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  const provider = sessionStorage.getItem(STORAGE_KEYS.AUTH_PROVIDER) as AuthProvider | null;

  if (!codeVerifier) {
    clearAuthStorage();
    throw new Error("No PKCE code verifier found — login flow may have been interrupted");
  }

  if (!provider) {
    clearAuthStorage();
    throw new Error("No auth provider found — login flow may have been interrupted");
  }

  const config = getProviderConfig(provider);
  if (!config) {
    clearAuthStorage();
    throw new Error(`Unknown provider: ${provider}`);
  }

  // Exchange the authorization code for tokens
  const tokenResponse = await exchangeCodeForTokens(config, code, codeVerifier);
  // Calculate expiration time (derives from JWT exp if expires_in is missing)
  const expiresAt = resolveExpiresAt(tokenResponse);

  // Store tokens
  const storedTokens: StoredTokens = {
    accessToken: tokenResponse.access_token,
    idToken: tokenResponse.id_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt,
    provider,
  };
  storeTokens(storedTokens);

  // Clean up PKCE and state from storage
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_PROVIDER);

  // Decode and return the ID token payload
  return decodeJwtPayload(tokenResponse.id_token);
}

/**
 * Exchange an authorization code for tokens.
 *
 * If the provider has a `tokenProxyUrl`, the request goes through the backend proxy
 * (which adds the client_secret from Secrets Manager). Otherwise, it calls the
 * provider's token endpoint directly (e.g., Cognito public client).
 */
async function exchangeCodeForTokens(config: ProviderConfig, code: string, codeVerifier: string): Promise<TokenResponse> {
  // If provider uses a backend proxy for token exchange (e.g., Federate with client_secret)
  if (config.tokenProxyUrl) {
    return exchangeCodeViaProxy(config, code, codeVerifier);
  }

  // Direct token exchange (e.g., Cognito public client — no secret needed)
  return exchangeCodeDirect(config, code, codeVerifier);
}

/**
 * Direct token exchange — FE calls the provider's token endpoint directly.
 * Used for public clients (no client_secret), e.g., Cognito.
 */
async function exchangeCodeDirect(config: ProviderConfig, code: string, codeVerifier: string): Promise<TokenResponse> {
  const tokenUrl = `${config.domain}/oauth2/token`;

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code: code,
    redirect_uri: config.redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

/**
 * Proxy token exchange — FE sends the auth code to the backend, which adds the
 * client_secret and calls the provider's token endpoint on our behalf.
 * Used for confidential clients (e.g., Federate).
 *
 * Backend endpoint expected: POST tokenProxyUrl
 * Body: { provider, code, code_verifier, redirect_uri, client_id }
 * Response: { access_token, id_token, refresh_token?, token_type, expires_in }
 */
async function exchangeCodeViaProxy(config: ProviderConfig, code: string, codeVerifier: string): Promise<TokenResponse> {
  const response = await fetch(config.tokenProxyUrl!, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      provider: config.provider,
      code,
      code_verifier: codeVerifier,
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
    }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange via proxy failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

/**
 * Refresh the access token using the refresh token.
 * Supports both direct refresh (Cognito) and proxy refresh (Federate).
 */
export async function refreshAccessToken(): Promise<boolean> {
  const tokens = getStoredTokens();
  if (!tokens?.refreshToken) {
    return false;
  }

  const config = getProviderConfig(tokens.provider);
  if (!config) {
    return false;
  }

  try {
    let tokenResponse: TokenResponse;

    // Use dedicated refresh endpoint if available, otherwise fall back to tokenProxyUrl
    const refreshUrl = config.refreshProxyUrl ?? config.tokenProxyUrl;

    if (refreshUrl) {
      // Proxy refresh — backend adds client_secret
      const response = await fetch(refreshUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: config.provider,
          grant_type: "refresh_token",
          refresh_token: tokens.refreshToken,
          client_id: config.clientId,
        }),
      });
      if (!response.ok) {
        console.error("Token refresh via proxy failed:", response.status);
        return false;
      }
      tokenResponse = await response.json();
    } else {
      // Direct refresh (e.g., Cognito public client)
      const tokenUrl = `${config.domain}/oauth2/token`;

      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.clientId,
        refresh_token: tokens.refreshToken,
      });

      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!response.ok) {
        console.error("Token refresh failed:", response.status);
        return false;
      }
      tokenResponse = await response.json();
    }

    const expiresAt = resolveExpiresAt(tokenResponse);
    const updatedTokens: StoredTokens = {
      accessToken: tokenResponse.access_token,
      // Some providers (e.g., Federate) don't return id_token on refresh — keep the existing one
      idToken: tokenResponse.id_token ?? tokens.idToken,
      refreshToken: tokenResponse.refresh_token ?? tokens.refreshToken,
      expiresAt,
      provider: tokens.provider,
    };
    storeTokens(updatedTokens);
    return true;
  } catch (error) {
    console.error("Token refresh error:", error);
    return false;
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Check if the user is currently authenticated (has valid, non-expired tokens).
 */
export function isAuthenticated(): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return false;
  // Consider tokens expired 30 seconds early to prevent edge cases
  return tokens.expiresAt > Date.now() + 30_000;
}

/**
 * Async, refresh-aware authentication check for app startup.
 *
 * Unlike {@link isAuthenticated} (a synchronous, refresh-free `expiresAt` check),
 * this attempts a token refresh when the stored token has expired but a
 * refresh_token is still available. This is what lets a returning user whose
 * id_token lapsed — but whose refresh_token is still valid — be silently
 * re-authenticated instead of bounced to the login page.
 *
 * @returns true if the user has (or was refreshed into) a valid session.
 */
export async function ensureAuthenticated(): Promise<boolean> {
  const tokens = getStoredTokens();
  if (!tokens) return false;

  // Still valid (with the same 30s early-expiry buffer as isAuthenticated) — no refresh needed.
  if (tokens.expiresAt > Date.now() + 30_000) {
    return true;
  }

  // Expired or about to expire: try to refresh. refreshAccessToken() returns false
  // when there is no refresh_token or the refresh request fails.
  return refreshAccessToken();
}

/**
 * Get the current access token.
 * Automatically refreshes if the token is expired or about to expire.
 * Returns null if no valid token is available.
 */
export async function getAccessToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  // If token is still valid (with 60s buffer), return it
  if (tokens.expiresAt > Date.now() + 60_000) {
    return tokens.accessToken;
  }

  // Try to refresh
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    const newTokens = getStoredTokens();
    return newTokens?.accessToken ?? null;
  }

  // Refresh failed — user needs to re-login
  return null;
}

/**
 * Get the token this provider's backend expects on `Authorization: Bearer`.
 *
 * - **Federate / IdPrism** → the **access_token**. It is the token the OIDC refresh grant renews,
 *   so a session can be extended for as long as the refresh token lives. The prod Federate
 *   deployment returns no id_token on refresh, which is why authenticating with the id_token
 *   capped every session at `idTokenAge` (1 hour) and logged users out.
 * - **Cognito** → the **id_token**, unchanged. A Cognito *access* token carries no `aud`, no
 *   `cognito:username` and no email, so `CognitoTokenVerifier` would reject it outright and the
 *   `cm_operator` lookup would then fail. Operators refresh directly against Cognito and are not
 *   part of the Federate migration.
 *
 * This is the single place that choice is made; {@code attachAuthHeaders} and the 401-retry path
 * both call it so they cannot drift apart.
 */
export async function getApiToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;
  return tokens.provider === "cognito" ? getIdToken() : getAccessToken();
}

/**
 * Get the current ID token.
 *
 * Still used for Cognito operators (see {@link getApiToken}) and for reading the user's identity
 * claims client-side via {@link getUserFromToken} — the id_token's intended purpose. Federate and
 * IdPrism callers no longer send this to the backend.
 *
 * Automatically refreshes if the token is expired or about to expire.
 * Returns null if no valid token is available.
 */
export async function getIdToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  // If token is still valid (with 60s buffer), return it
  if (tokens.expiresAt > Date.now() + 60_000) {
    return tokens.idToken;
  }
  // Try to refresh
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    const newTokens = getStoredTokens();
    return newTokens?.idToken ?? null;
  }

  // Refresh failed — user needs to re-login
  return null;
}

/**
 * Get user information from the stored ID token.
 * Returns null if no valid tokens are stored.
 */
export function getUserFromToken(): IdTokenPayload | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  try {
    return decodeJwtPayload(tokens.idToken);
  } catch {
    return null;
  }
}

/**
 * Resolve a stable per-user key for namespacing client-side storage (e.g.,
 * recent-visit history). Uses the ID token `sub` claim, which is stable across
 * logins and present for all providers. Falls back to "anonymous" when no token
 * is available (dev bypass) or `sub` is missing.
 */
export function getHistoryUserKey(): string {
  // The `?? "anonymous"` is load-bearing: getUserFromToken() returns null when no
  // token is stored (dev bypass), and a decoded token may lack `sub` at runtime
  // even though the type marks it required. Do not remove.
  return getUserFromToken()?.sub ?? "anonymous";
}

/**
 * Get the current auth provider.
 */
export function getCurrentProvider(): AuthProvider | null {
  const tokens = getStoredTokens();
  return tokens?.provider ?? null;
}

/**
 * Extract normalized user info from the ID token, aware of different provider claim formats.
 *
 * Claim names differ by provider, and the Federate ones are not the OIDC-standard spellings:
 * - Cognito:  `cognito:username`, `email`, `given_name`, `family_name`
 * - Federate (Midway):  `sub` (Amazon alias), `EMAIL`, `GIVEN_NAME`, `FAMILY_NAME`
 * - Federate (IdPrism): `UUID`, `email`, `first_name`, `last_name`
 *
 * Each field therefore reads the alternatives in turn. Before this, only the lowercase
 * OIDC spellings were read, so `givenName` and `familyName` were `undefined` for both Federate
 * populations and the displayed name came entirely from the backend's `get_user_info`.
 *
 * That now matters: the backend authenticates the access_token, which carries no name claims, so
 * `get_user_info` returns them as null and the display name has to come from the id_token the
 * browser still holds. `normalizeUserInfoResponse` only overrides a field when the backend's
 * value is truthy, so a null `firstName` leaves the value resolved here intact.
 */
export function extractUserInfo(tokenPayload: IdTokenPayload, provider: AuthProvider): Partial<IUser> {
  // Provider-specific username extraction
  const username = resolveUsername(tokenPayload, provider);

  const claim = (...names: string[]): string | undefined => {
    for (const name of names) {
      const value = asString((tokenPayload as Record<string, unknown>)[name]);
      if (value) return value;
    }
    return undefined;
  };

  return {
    username,
    email: claim("EMAIL", "email"),
    sub: asString(tokenPayload.sub),
    givenName: claim("GIVEN_NAME", "first_name", "given_name"),
    familyName: claim("FAMILY_NAME", "last_name", "family_name"),
    authProvider: provider,
  };
}

/**
 * Raw shape of the backend `get_user_info` response.
 *
 * NOTE: the backend currently returns these fields at the top level (NOT wrapped in the
 * usual `{ data, isSuccess }` envelope), and uses its own field names (`userName`,
 * `firstName`). {@link normalizeUserInfoResponse} tolerates both the flat and the
 * enveloped shape so the store always receives a clean {@link IUser}.
 */
interface RawUserInfo {
  userName?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: IUser["role"];
  roles?: IUser["roles"];
  activated?: boolean;
}

/**
 * Normalize the backend `get_user_info` response into a partial {@link IUser}.
 *
 * Handles both response shapes:
 * - Flat (current backend): `{ userName, firstName, role, ... }`
 * - Enveloped (mock / once the backend adds the wrapper): `{ data: { ... } }`
 *
 * Also maps backend field names (`userName`, `firstName`) onto the FE model
 * (`username`, `givenName`).
 */
export function normalizeUserInfoResponse(res: unknown): Partial<IUser> {
  // Unwrap the `{ data }` envelope if present; otherwise treat the body as flat.
  const raw = (res && typeof res === "object" && "data" in res ? (res as { data: RawUserInfo }).data : res) as RawUserInfo | undefined;
  if (!raw || typeof raw !== "object") return {};

  const normalized: Partial<IUser> = {};
  const username = asString(raw.username) ?? asString(raw.userName);
  if (username) normalized.username = username;
  const givenName = asString(raw.firstName);
  if (givenName) normalized.givenName = givenName;
  const familyName = asString(raw.lastName);
  if (familyName) normalized.familyName = familyName;
  const email = asString(raw.email);
  if (email) normalized.email = email;
  if (raw.role) normalized.role = raw.role;
  if (raw.roles) normalized.roles = raw.roles;
  if (typeof raw.activated === "boolean") normalized.activated = raw.activated;

  return normalized;
}

/**
 * Resolve the username from the token payload based on the auth provider.
 * Each provider may store the username in different claims.
 */
function resolveUsername(payload: IdTokenPayload, provider: AuthProvider): string {
  switch (provider) {
    case "cognito":
      // Cognito stores username in a custom claim
      return asString(payload["cognito:username"]) ?? asString(payload.email) ?? payload.sub;

    case "idprism":
      // IdPrism goes through Federate but authenticates against an IdPrism user pool.
      // The token is issued by Federate; sub may be the IdPrism username or a UUID.
      // Try preferred_username first, then email, then fall back to sub.
      return asString(payload.preferred_username as string) ?? asString(payload.email) ?? payload.sub;

    case "federate":
      // Federate returns the Amazon alias as `sub` (e.g., "jianyihp")
      // Claims: sub, groups, amr — no email or name claims available
      return payload.sub;

    default:
      return asString(payload.email) ?? payload.sub;
  }
}

/** Safely cast unknown values to string or return undefined. */
function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Get the URL the user was on before being redirected to login, then clear it.
 * Returns "/" if no return URL was stored.
 */
export function getAndClearReturnUrl(): string {
  const returnUrl = sessionStorage.getItem(STORAGE_KEYS.RETURN_URL);
  sessionStorage.removeItem(STORAGE_KEYS.RETURN_URL);
  return returnUrl ?? "/";
}

/**
 * Build the provider logout (end-session) URL, or return `null` when the provider
 * has no usable logout endpoint. Hitting this endpoint is what terminates the
 * upstream provider session; without it the provider silently re-authenticates the
 * user on the next login, so the app appears to "not log out".
 *
 * Behavior by provider:
 *
 * - **Cognito**: `<domain>/logout?client_id=…&logout_uri=<app-origin>`. The
 *   `logout_uri` (redirectUri without `/callback`) must be registered as an
 *   Allowed sign-out URL on the Cognito app client.
 *
 * - **IdPrism** (CM-engineer login, brokered through Federate): IdPrism DOES
 *   expose an RP-initiated logout endpoint, but its URL is tenant/stage-specific
 *   and lives in `config.logoutUrl` (see env.ts). We append the app origin as the
 *   post-logout redirect so the user returns to the app after the IdPrism session
 *   is torn down. That redirect URI must be allow-listed on the IdPrism/Federate
 *   enrollment profile or the redirect 403s. When `logoutUrl` is unset (not yet
 *   confirmed/allow-listed), returns `null` → local-only logout, never a guess.
 *
 * - **Federate / Midway** (internal login): Federate exposes NO end-session
 *   endpoint (its OIDC discovery advertises none; it "will never redirect the user
 *   to the logout endpoint of a brokered provider"), so `<federate-domain>/logout`
 *   is not a real endpoint and 403s. We also intentionally leave the global Midway
 *   SSO session alone. Returns `null` → clearing local tokens IS the logout.
 */
export function buildProviderLogoutUrl(config: ProviderConfig): string | null {
  const appOrigin = config.redirectUri.replace("/callback", "");

  if (config.provider === "cognito") {
    const logoutParams = new URLSearchParams({
      client_id: config.clientId,
      logout_uri: appOrigin,
    });
    return `${config.domain}/logout?${logoutParams.toString()}`;
  }

  // IdPrism: only when the tenant end-session URL has been configured.
  if (config.logoutUrl) {
    const logoutParams = new URLSearchParams({
      client_id: config.clientId,
      post_logout_redirect_uri: appOrigin,
    });
    return `${config.logoutUrl}?${logoutParams.toString()}`;
  }

  // Federate/Midway, or IdPrism not yet configured — no provider logout to perform.
  return null;
}

/**
 * Log out the user. Erases all local user data, then hands off to the backend's
 * `/auth/logout` so the other half of the session is ended too.
 *
 * There are two sessions, and this app can only clear one of them:
 *
 * - **This app's** tokens in localStorage — `clearAllUserData()` below.
 * - **The edge gate's** `radarplus_id_token` cookie, set by the backend's
 *   `/auth/callback`. It is `HttpOnly` and scoped to the parent domain, so no
 *   JavaScript here can remove it.
 *
 * Clearing only the first is what produced the inconsistency this replaces: the
 * cookie survived, so the Lambda@Edge gate still admitted the next request, the
 * app loaded with no tokens, and the user landed on this app's login page —
 * signed out of the app while still authenticated at the edge, and a different
 * screen from the one a first-time visitor sees.
 *
 * The backend endpoint clears the cookie and then sends the user to the provider
 * chooser, which is exactly where a first visit lands. `provider` is passed on so
 * it can also end the provider-side session where that is possible: Cognito has a
 * hosted logout endpoint, and Federate deliberately does not, so for Federate and
 * IdPrism the backend instead forces re-authentication with `prompt=login`.
 *
 * This is correct on ungated stages too. There the cookie does not exist, the
 * clear is a no-op, and the chooser round trip returns through
 * `?provider=<chosen>`, which this app's own provider hint picks up to resume its
 * normal sign-in.
 */
export function logout(): void {
  const tokens = getStoredTokens();
  const provider = tokens?.provider;

  clearAllUserData();

  const logoutUrl = provider ? `${backendOrigin}/auth/logout?provider=${encodeURIComponent(provider)}` : `${backendOrigin}/auth/logout`;
  window.location.href = logoutUrl;
}
